import React, { useState, useCallback, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";

// Reusa o CSS dos consolidados: mesma tabela, mesma paginação, nada de estilo novo só para esta tela.
import styles from "../classConsolidatedData/classConsolidatedData.module.css";
import DatabaseIcon from "../../../assets/icons/manage-columns.svg";
import ArrowLeft from "../../../assets/icons/arrow-left.svg";
import loadingGif from "../../../assets/loading.gif";
import Button from "../../../components/button/button";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import { useInstitution } from "../../../context/InstitutionContext";
import { getRawImportedData } from "../../../services/import";

const PAGE_SIZE = 50;

/**
 * Dados de um arquivo importado que não alimentou nenhum indicador: as linhas foram guardadas como
 * vieram (colunas dinâmicas, na ordem original do arquivo) e ficam disponíveis para consulta.
 */
const RawImportedData = () => {
  const { institution } = useInstitution();
  const [searchParams] = useSearchParams();

  const fileId = searchParams.get("fileId");
  const fileNameParam = searchParams.get("fileName");

  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [fileName, setFileName] = useState(fileNameParam || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async (page) => {
    if (!institution?.id || !fileId) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await getRawImportedData({
        fileId,
        institutionId: institution.id,
        page,
        pageSize: PAGE_SIZE
      });
      setColumns(data.columns || []);
      setRows(data.rows || []);
      setTotalRows(data.totalRows || 0);
      if (data.fileName) {
        setFileName(data.fileName);
      }
    } catch (err) {
      setError(typeof err === "string" ? err : "Não foi possível carregar os dados desse arquivo.");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [institution?.id, fileId]);

  useEffect(() => {
    setCurrentPage(1);
    fetchRows(1);
  }, [fetchRows]);

  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

  const handlePageClick = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) {
      return;
    }
    setCurrentPage(page);
    fetchRows(page);
  };

  const columnCount = columns.length || 1;

  return (
    <div className={styles.consolidatedDataContent}>
      <HeaderTitle title={fileName ? `Dados de ${fileName}` : "Dados importados"} icon={DatabaseIcon}>
        <Link to="/app/importar-dados">
          <Button icon={ArrowLeft} text="Voltar" size="normal" variation="outline" />
        </Link>
      </HeaderTitle>

      <div className={styles.consolidatedDataBody}>
        <div className={styles.tableContainer}>
          <p style={{ padding: "12px 16px", color: "#64748b", margin: 0 }}>
            Este arquivo foi importado e está guardado, mas ainda não possui dados suficientes para gerar
            indicadores. Conforme arquivos com dados detalhados forem importados, os indicadores passam a
            ser calculados automaticamente.
          </p>

          <div className={styles.tableWrapper}>
            <table className={styles.consolidatedDataTable}>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columnCount} style={{ textAlign: "center", padding: "40px" }}>
                      <img src={loadingGif} alt="Carregando..." className={styles.loadingIcon} />
                      <p>Carregando dados do arquivo...</p>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={columnCount} style={{ textAlign: "center", padding: "40px", color: "#b91c1c" }}>
                      {error}
                    </td>
                  </tr>
                ) : rows.length > 0 ? (
                  rows.map((row, index) => (
                    <tr key={`row-${(currentPage - 1) * PAGE_SIZE + index}`}>
                      {columns.map((column) => (
                        <td key={column}>{row[column] || "-"}</td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columnCount} style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                      Nenhum dado encontrado para este arquivo
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            {totalPages > 1 && (
              <>
                <button
                  className={styles.paginationArrow}
                  onClick={() => handlePageClick(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Página anterior"
                >
                  ←
                </button>
                <span className={styles.paginationDots}>
                  Página {currentPage} de {totalPages} ({totalRows} linha(s))
                </span>
                <button
                  className={styles.paginationArrow}
                  onClick={() => handlePageClick(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Próxima página"
                >
                  →
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RawImportedData;
